import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { Fragment, useState } from 'react'
import { Controller } from 'react-hook-form'
import { EditPencilIcon } from 'Icons/Edit'
import { TrashIcon } from 'Icons/Trash'
import { FormLangTab } from 'Components/Tabs'
import { InputUI } from 'Components/UI'
import { APP } from 'Constants/App'
import { formLangTab } from 'Data/app'
import { useCenterHistoryForm2 } from '../Hooks/useCenterHistoryForm2'

const getFieldSuffix = (langCode: string): string => {
  return langCode === 'Kr' ? 'Ko' : langCode
}

export const HistoryForm2 = () => {
  const {
    form,
    onSubmit,
    langError,
    allFieldsFilled,
    isSubmitting,
    histories,
    selectedHistoryId,
    isCreateMode,
    handleSelectHistory,
    handleCreateNew,
    handleDelete,
    handleEditClick,
    isDeleting,
  } = useCenterHistoryForm2()

  const { control, handleSubmit, clearErrors, setValue } = form

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [historyToDelete, setHistoryToDelete] = useState<number | null>(null)

  const handleControlledChange = (
    name: 'year' | 'titleUz' | 'titleRu' | 'titleKo' | 'descriptionUz' | 'descriptionRu' | 'descriptionKo',
    value: string,
  ) => {
    setValue(name, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    if (value.trim()) {
      clearErrors(name)
    }
  }

  const submitForm = handleSubmit(onSubmit)

  // Open delete confirmation dialog
  const openDeleteDialog = (historyId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setHistoryToDelete(historyId)
    setDeleteDialogOpen(true)
  }

  // Handle confirmed delete
  const confirmDelete = () => {
    if (historyToDelete) {
      handleDelete(historyToDelete)
      setDeleteDialogOpen(false)
      setHistoryToDelete(null)
    }
  }

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setHistoryToDelete(null)
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Button variant='contained' fullWidth onClick={handleCreateNew}>
                {APP.ADD_HISTORY || 'Create new history'}
              </Button>

              <Divider />

              <Stack spacing={1.5}>
                {histories.length === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    No history items found.
                  </Typography>
                ) : (
                  histories.map(item => {
                    const isActive = selectedHistoryId === item.historyId && !isCreateMode

                    return (
                      <Box
                        key={item.historyId}
                        sx={{
                          border: theme => `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 1.5,
                          transition: '0.2s',
                          bgcolor: isActive ? 'action.selected' : 'background.paper',
                        }}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
                          <Box sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }} onClick={() => handleSelectHistory(item.historyId)}>
                            <Typography variant='subtitle2' noWrap>
                              {item.year || '-'}
                            </Typography>
                            <Typography variant='body2' color='text.secondary' noWrap>
                              {item.titleUz || item.titleRu || item.titleKo || 'Untitled'}
                            </Typography>
                          </Box>

                          <Stack direction='row' spacing={1}>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={e => {
                                e.stopPropagation()
                                handleEditClick(item.historyId)
                              }}>
                              <EditPencilIcon />
                            </Button>

                            <Button
                              size='small'
                              color='error'
                              variant='outlined'
                              disabled={isDeleting}
                              onClick={e => openDeleteDialog(item.historyId, e)}>
                              <TrashIcon />
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    )
                  })
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <FormLangTab
          error={langError}
          render={lang => {
            return (
              <form
                onSubmit={e => {
                  e.preventDefault()
                  submitForm()
                }}>
                <Grid container rowSpacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name='year'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <InputUI
                          value={field.value ?? ''}
                          label={APP.YEAR || 'Year'}
                          placeholder='1992.5'
                          error={!!error?.message}
                          helperText={error?.message}
                          onBlur={field.onBlur}
                          onChange={(e: any) => {
                            const value = e?.target?.value ?? e ?? ''
                            handleControlledChange('year', value)
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {formLangTab.map(({ id }) => {
                    const langSuffix = getFieldSuffix(id)
                    const titleFieldName = `title${langSuffix}` as 'titleUz' | 'titleRu' | 'titleKo'
                    const descriptionFieldName = `description${langSuffix}` as 'descriptionUz' | 'descriptionRu' | 'descriptionKo'

                    return (
                      <Fragment key={id}>
                        {lang === id && (
                          <>
                            <Grid item xs={12}>
                              <Controller
                                name={titleFieldName}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <InputUI
                                    value={field.value ?? ''}
                                    label={`${APP.TITLE} (${id})`}
                                    placeholder={APP.ENTER_TITLE}
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    onBlur={field.onBlur}
                                    onChange={(e: any) => {
                                      const value = e?.target?.value ?? e ?? ''
                                      handleControlledChange(titleFieldName, value)
                                    }}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Controller
                                name={descriptionFieldName}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <TextField
                                    value={field.value ?? ''}
                                    label={`${APP.TEXT || 'Description'} (${id})`}
                                    multiline
                                    minRows={5}
                                    fullWidth
                                    error={!!error?.message}
                                    helperText={error?.message}
                                    onBlur={field.onBlur}
                                    onChange={e => {
                                      const value = e.target.value
                                      handleControlledChange(descriptionFieldName, value)
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                      </Fragment>
                    )
                  })}

                  <Grid item xs={12}>
                    <Stack direction='row' spacing={2}>
                      <Button
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        disabled={isSubmitting}
                        onClick={submitForm}
                        type='button'
                        size='large'
                        variant='contained'
                        fullWidth
                        sx={{
                          opacity: allFieldsFilled ? 1 : 0.7,
                        }}>
                        {isCreateMode ? APP.ADD_HISTORY || 'Create' : APP.SAVE || APP.PUBLISH || 'Save'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            )
          }}
        />
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'>
        <DialogTitle id='delete-dialog-title'>Удалить запись?</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Вы уверены, что хотите удалить эту запись? После удаления восстановить её будет невозможно.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color='primary'>
            {APP.CANCEL || 'Cancel'}
          </Button>
          <Button onClick={confirmDelete} color='error' variant='contained' autoFocus>
            {APP.REMOVE || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}
